import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { reportAPI, notificationAPI } from '../services/api';
import { toast } from 'react-toastify';
import Icon from '../components/Icon';

const RANGES = [
  { value: 'all', label: 'All time' },
  { value: 'last_7_days', label: '7D' },
  { value: 'last_30_days', label: '30D' },
  { value: 'last_90_days', label: '90D' },
  { value: 'current_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'current_year', label: 'This year' },
  { value: 'custom', label: 'Custom' }
];

const REGION_COLORS = ['#037b90', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

function greetingFor(hour) {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const formatTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  const diffInWeeks = Math.floor(diffInDays / 7);
  return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
};

function DeltaChip({ value, suffix = '%' }) {
  const up = value >= 0;
  return (
    <span className={`delta ${up ? 'delta-up' : 'delta-down'}`}>
      <Icon name={up ? 'trending-up' : 'trending-down'} />
      {up ? '+' : ''}{value}{suffix} <span style={{ opacity: 0.7, fontWeight: 400 }}>vs prev</span>
    </span>
  );
}

function KpiTile({ label, value, delta, icon, color }) {
  return (
    <div className="kpi-tile">
      <div className="kpi-top">
        <div>
          <div className="kpi-label">{label}</div>
          <div className="kpi-value">{value}</div>
        </div>
        <div className="kpi-icon" style={{ background: `${color}1a`, color }}>{icon}</div>
      </div>
      {delta !== undefined && <DeltaChip value={delta} />}
    </div>
  );
}

export default function AdminAnalyticsDashboard() {
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const stored = JSON.parse(localStorage.getItem('user') || '{}');
  const displayName = stored.full_name || stored.firstName || stored.username || stored.email || 'there';
  const firstName = displayName.split(' ')[0];

  const load = async (r = range, cs = customStart, ce = customEnd) => {
    setLoading(true);
    try {
      const params = {};
      if (r === 'custom') {
        if (cs) params.startDate = cs;
        if (ce) params.endDate = ce;
        if (!cs && !ce) params.range = 'all';
        else params.range = 'custom';
      } else {
        params.range = r;
      }
      const [statsRes, notifRes] = await Promise.all([
        reportAPI.getDashboard(params),
        notificationAPI.getRecent(6, true)
      ]);
      setStats(statsRes.data);
      setNotifications(notifRes.data || []);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeRange = (r) => {
    setRange(r);
    load(r, customStart, customEnd);
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) {
      toast.error('Select both start and end dates');
      return;
    }
    setRange('custom');
    load('custom', customStart, customEnd);
  };

  const performanceData = (stats?.monthlyPerformance || []).map((m) => ({
    month: m.month_name,
    Admissions: parseInt(m.admissions, 10) || 0,
    Enrollments: parseInt(m.enrollments, 10) || 0,
    Revenue: parseFloat(m.revenue) || 0
  }));

  const topRecruiters = (stats?.topRecruiters || []).slice(0, 5).map((r) => ({
    name: (r.full_name || 'Recruiter').split(' ')[0],
    commission: parseFloat(r.total_commission || 0)
  }));

  const regionData = (stats?.regionalData || []).map((item, i) => ({
    name: item.region,
    value: parseInt(item.count, 10) || 0,
    color: REGION_COLORS[i % REGION_COLORS.length]
  }));

  const ov = stats?.overview || {};
  const d = stats?.deltas || {};

  const exportReport = () => {
    const txt = `Admin Dashboard Report - ${new Date().toLocaleDateString()}\n\nOverview:\n- Active Recruiters: ${ov.activeRecruiters || 0}\n- Admissions: ${ov.totalAdmissions || 0}\n- Enrollments: ${ov.totalEnrollments || 0}\n- Commissions: KES ${Number(ov.totalCommissions || 0).toFixed(2)}\n- Pending Payments: ${ov.pendingPayments || 0}\n\nGenerated: ${new Date().toLocaleString()}`;
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dashboard_report_${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (loading && !stats) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }} />
          <h3 style={{ color: '#64748b', fontWeight: 600 }}>Loading Dashboard…</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content admin-analytics">
      {/* Hero / greeting */}
      <div className="analytics-hero">
        <div>
          <div className="hero-greeting">{greetingFor(new Date().getHours())},</div>
          <div className="hero-name">Welcome back, {firstName}</div>
          <div className="hero-date">{new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
        <div className="hero-actions">
          <button className="btn btn-outline btn-sm" onClick={exportReport}><Icon name="bar-chart" /> Export</button>
          <button className="btn btn-primary btn-sm" onClick={() => load()}><Icon name="refresh" /> Refresh</button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <span className="filter-label"><Icon name="calendar" /> Period:</span>
        {RANGES.filter((r) => r.value !== 'custom').map((r) => (
          <button
            key={r.value}
            className={`filter-pill ${range === r.value ? 'active' : ''}`}
            onClick={() => changeRange(r.value)}
          >
            {r.label}
          </button>
        ))}
        <button className={`filter-pill ${range === 'custom' ? 'active' : ''}`} onClick={() => changeRange('custom')}>Custom</button>
        {range === 'custom' && (
          <span className="custom-range">
            <input type="date" className="form-input" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
            <span>→</span>
            <input type="date" className="form-input" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            <button className="btn btn-primary btn-sm" onClick={applyCustom}>Apply</button>
          </span>
        )}
        {stats?.period && range !== 'custom' && (
          <span className="filter-current">{stats.period.start} → {stats.period.end}</span>
        )}
      </div>

      {/* KPI tiles */}
      <div className="kpi-grid">
        <KpiTile label="Active Recruiters" value={ov.activeRecruiters || 0} icon={<Icon name="users" />} color="#037b90" />
        <KpiTile label="Admissions" value={ov.totalAdmissions || 0} delta={d.admissions} icon={<Icon name="user" />} color="#3b82f6" />
        <KpiTile label="Enrollments" value={ov.totalEnrollments || 0} delta={d.enrollments} icon={<Icon name="user-check" />} color="#10b981" />
        <KpiTile label="Commissions (KES)" value={Number(ov.totalCommissions || 0).toLocaleString()} delta={d.revenue} icon={<Icon name="dollar" />} color="#f59e0b" />
        <KpiTile label="Pending Payments" value={ov.pendingPayments || 0} icon={<Icon name="clock" />} color="#8b5cf6" />
      </div>

      {/* Charts row 1 */}
      <div className="dash-grid">
        <div className="card">
          <div className="card-header"><div><h2 className="card-title">Performance Trend</h2><p className="card-subtitle">Admissions vs Enrollments (last 6 months)</p></div></div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={performanceData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gAdm" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                <linearGradient id="gEnr" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="95%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="Admissions" stroke="#3b82f6" fill="url(#gAdm)" strokeWidth={2} />
              <Area type="monotone" dataKey="Enrollments" stroke="#10b981" fill="url(#gEnr)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header"><div><h2 className="card-title">Top Recruiters</h2><p className="card-subtitle">By commission earned</p></div></div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topRecruiters} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="commission" fill="#037b90" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="dash-grid">
        <div className="card">
          <div className="card-header"><div><h2 className="card-title">Regional Distribution</h2><p className="card-subtitle">Active recruiters by county</p></div></div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={regionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {regionData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header"><div><h2 className="card-title">Recent Activity</h2><p className="card-subtitle">Latest notifications & updates</p></div></div>
          <div className="activity-feed">
            {notifications.length === 0 && <p style={{ color: '#94a3b8', padding: '12px' }}>No recent activity.</p>}
            {notifications.map((n) => (
              <div key={n.id} className="activity-item">
                <div className="activity-title">{n.title}</div>
                <div className="activity-desc">{n.message}</div>
                <div className="activity-time">{formatTimeAgo(n.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
